export type GraphicsQuality = 'low' | 'balanced' | 'high';
export interface GraphicsProfile {
  name: GraphicsQuality;
  maxDpr: number;
  maxPixels: number;
  shadowSize: number;
  surfacePixels: number;
  surfaceMaxSize: number;
  loadConcurrency: number;
  /**
   * The post-processing chain (`engine/Post.ts`): the scene drawn in high
   * range, then graded, vignetted and anti-aliased. Low draws straight to
   * the screen, as everything did before the chain.
   */
  post: boolean;
  /** Fire, flashes and the lit windows bloom into the air round them. */
  bloom: boolean;
  /** Ambient occlusion from the depth buffer: dark where walls meet the ground. */
  ao: boolean;
  /** The sky reflected in water, glass and wet roofs (a prefiltered environment map). */
  reflections: boolean;
  /**
   * Smoke and fire drawn in a pass of their own over the scene's depth, so
   * they fade where they meet the city instead of being cut by it.
   */
  softParticles: boolean;
  /**
   * The resolution that pass is drawn at, as a share of the view's: smoke is
   * soft, and at half it costs a quarter of the overdraw.
   */
  particleScale: number;
  /**
   * Point lights a blast can lend the city (`Atmosphere`). Each costs every
   * lit pixel of every frame, lit or not: about a millisecond on integrated
   * graphics.
   */
  flashLights: number;
  /** SMAA's three passes rather than FXAA's one, where there is a chain. */
  smaa: boolean;
}

const profiles: Record<GraphicsQuality, GraphicsProfile> = {
  low: {
    name: 'low', maxDpr: 1, maxPixels: 1_000_000, shadowSize: 1024, surfacePixels: 32, surfaceMaxSize: 1024,
    loadConcurrency: 3,
    post: false, bloom: false, ao: false, reflections: false, softParticles: false, particleScale: 1, flashLights: 0, smaa: false,
  },
  balanced: {
    name: 'balanced', maxDpr: 1.5, maxPixels: 1_300_000, shadowSize: 1536, surfacePixels: 64, surfaceMaxSize: 2048,
    loadConcurrency: 6,
    post: true, bloom: true, ao: false, reflections: false, softParticles: true, particleScale: .5, flashLights: 2, smaa: false,
  },
  high: {
    name: 'high', maxDpr: 2, maxPixels: 4_000_000, shadowSize: 2048, surfacePixels: 96, surfaceMaxSize: 3072,
    loadConcurrency: 8,
    post: true, bloom: true, ao: true, reflections: true, softParticles: true, particleScale: 1, flashLights: 4, smaa: true,
  },
};

const isQuality = (q: string | null | undefined): q is GraphicsQuality => q === 'low' || q === 'balanced' || q === 'high';

/** Where the player's own choice is kept, on this device. */
const STORED = 'tb.graphics';

/** The player's chosen quality, if they chose one. */
export function storedGraphics(): GraphicsQuality | null {
  try {
    const q = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORED);
    return isQuality(q) ? q : null;
  } catch { return null; }
}

/**
 * Keep the player's choice (null: let the device decide). It takes effect
 * when the page next loads: every shader is compiled for the profile it
 * started with.
 */
export function storeGraphics(quality: GraphicsQuality | null): void {
  try {
    if (quality) localStorage.setItem(STORED, quality); else localStorage.removeItem(STORED);
  } catch { /* private window: the choice lasts until the page closes */ }
}

/** Explicit URL override is useful on laptops whose GPU is weaker than CPU
 * hints suggest; then the player's own choice; then the device's hints.
 * Missing browser hints default to balanced, not high. */
export function chooseGraphicsProfile(
  override: string | null,
  hints: { memory?: number; cores?: number; coarse?: boolean; saveData?: boolean },
  stored: string | null = null,
): GraphicsProfile {
  if (isQuality(override)) return profiles[override];
  if (isQuality(stored)) return profiles[stored];
  const constrained = hints.saveData || hints.coarse || (hints.memory != null && hints.memory <= 4)
    || (hints.cores != null && hints.cores <= 4);
  return profiles[constrained ? 'low' : 'balanced'];
}

function deviceHints() {
  const nav = typeof navigator === 'undefined' ? undefined : navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
  return {
    memory: nav?.deviceMemory, cores: nav?.hardwareConcurrency, saveData: nav?.connection?.saveData,
    coarse: typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches,
  };
}

/** What this device would get if the player left it to decide. */
export function automaticGraphics(): GraphicsQuality {
  return chooseGraphicsProfile(null, deviceHints()).name;
}

let selected: GraphicsProfile | undefined;
export function graphicsProfile(): GraphicsProfile {
  if (selected) return selected;
  return selected = chooseGraphicsProfile(
    typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('graphics'),
    deviceHints(), storedGraphics(),
  );
}

export function renderPixelRatio(width: number, height: number, dpr: number, profile = graphicsProfile()): number {
  return Math.min(Math.max(.1, dpr || 1), profile.maxDpr, Math.sqrt(profile.maxPixels / Math.max(1, width * height)));
}

export function surfacePixelsPerTile(width: number, height: number, profile = graphicsProfile()): number {
  return Math.min(profile.surfacePixels, profile.surfaceMaxSize / Math.max(1, width, height));
}
